// src/components/tutor/mobile/MobileTutorAvailability.js
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { flushSync } from "react-dom";
import { auth } from "../../../firebaseConfig";
import "./MobileAvailability.css";

import {
  createTutorAvailabilityBlock,
  deleteTutorAvailabilityBlock,
  fetchTutorBlocksInRange,
  updateTutorAvailabilityBlock,
} from "../availability/tutorAvailabilityFirestore";
import { fetchDayConfigsForDates } from "../../admin/scheduling/schedulingApi";
import {
  startOfMonth,
  endOfMonthExclusive,
  addMonths,
  monthGridDays,
  buildTimeOptions,
  fmtTimeLabel,
  sameYMD,
  isDisabledDay,
  isWithinSevenDays,
  resolveDayTimelineHours,
} from "../availability/tutorAvailabilityUtils";

// ─── Layout constants ─────────────────────────────────────────────────────────

const PAST_MONTHS_INIT = 2;
const LOAD_MORE_PAST   = 3;
const INIT_FUTURE      = 4;
const LOAD_MORE_BY     = 3;

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function toDate(ts) {
  return ts?.toDate ? ts.toDate() : new Date(ts);
}

function formatFullDate(date) {
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

function dateKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function monthKeyOf(date) {
  return `${date.getFullYear()}-${date.getMonth()}`;
}

function captureViewportAnchor(containerEl) {
  if (!containerEl) return null;
  const containerRect = containerEl.getBoundingClientRect();
  const monthEls = containerEl.querySelectorAll(".mav-month-block");
  for (let i = 0; i < monthEls.length; i += 1) {
    const node = monthEls[i];
    const rect = node.getBoundingClientRect();
    if (rect.bottom > containerRect.top) {
      return {
        key: node.getAttribute("data-month-key"),
        top: rect.top - containerRect.top,
      };
    }
  }
  return null;
}

// ─── Day cell ─────────────────────────────────────────────────────────────────

function DayCell({ date, month, blockMap, dayConfigsMap, selectedDate, today, onDateClick }) {
  if (date.getMonth() !== month.getMonth()) {
    return <div className="mav-day-empty" />;
  }

  const disabled   = isDisabledDay(date, dayConfigsMap);
  const hasBlock   = !!blockMap[dateKey(date)];
  const isSelected = !!(selectedDate && sameYMD(selectedDate, date));
  const isToday    = sameYMD(date, today);

  let circleMod = "";
  if (isSelected)    circleMod = " mav-day-circle--selected";
  else if (hasBlock) circleMod = " mav-day-circle--available";
  else if (isToday)  circleMod = " mav-day-circle--today";

  let numMod = "";
  if (disabled)        numMod = " mav-day-num--disabled";
  else if (isSelected) numMod = " mav-day-num--selected";
  else if (isToday)    numMod = " mav-day-num--today";

  return (
    <button
      className={`mav-day${disabled ? " mav-day--disabled" : ""}`}
      onClick={() => onDateClick(date)}
      disabled={disabled}
      aria-label={date.toDateString()}
    >
      <div className={`mav-day-circle${circleMod}`}>
        <span className={`mav-day-num${numMod}`}>{date.getDate()}</span>
      </div>
    </button>
  );
}

// ─── Month block ──────────────────────────────────────────────────────────────

function MonthBlock({ month, blockMap, dayConfigsMap, selectedDate, today, onDateClick, blockRef }) {
  const days       = useMemo(() => monthGridDays(month), [month]);
  const monthLabel = month.toLocaleDateString("en-US", { month: "long" });
  const yearLabel  = month.getFullYear();
  const monthKey   = monthKeyOf(month);

  return (
    <div className="mav-month-block" data-month-key={monthKey} ref={blockRef}>
      <div className="mav-month-label">
        <span className="mav-month-name">{monthLabel}</span>
        <span className="mav-month-year">{yearLabel}</span>
      </div>
      <div className="mav-days-grid">
        {days.map((date, i) => (
          <DayCell
            key={i}
            date={date}
            month={month}
            blockMap={blockMap}
            dayConfigsMap={dayConfigsMap}
            selectedDate={selectedDate}
            today={today}
            onDateClick={onDateClick}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MobileTutorAvailability() {
  const uid = auth.currentUser?.uid;

  const BASE  = useMemo(() => startOfMonth(new Date()), []);
  const today = useMemo(() => new Date(), []);

  const [pastCount,   setPastCount]   = useState(PAST_MONTHS_INIT);
  const [futureCount, setFutureCount] = useState(INIT_FUTURE);

  const monthList = useMemo(() => {
    const total = pastCount + 1 + futureCount;
    return Array.from({ length: total }, (_, i) => addMonths(BASE, i - pastCount));
  }, [pastCount, futureCount, BASE]);

  // ── Data ──
  const [blocks,        setBlocks]        = useState([]);
  const [dayConfigsMap, setDayConfigsMap] = useState({});
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const blockMap = useMemo(() => {
    const map = {};
    blocks.forEach((b) => {
      const d = toDate(b.start);
      map[dateKey(d)] = b;
    });
    return map;
  }, [blocks]);

  // ── Selection ──
  const [selectedDate, setSelectedDate] = useState(null);
  const [editBlock,    setEditBlock]    = useState(null);
  const [isLocked,     setIsLocked]     = useState(false);

  // ── Form ──
  const [startMin, setStartMin] = useState(9 * 60);
  const [endMin,   setEndMin]   = useState(17 * 60);
  const timeOptions = useMemo(() => buildTimeOptions(), []);

  // ── Refs ──
  const containerRef      = useRef(null);
  const currentMonthRef   = useRef(null);
  const loadedEndRef      = useRef(INIT_FUTURE);
  const loadedStartRef    = useRef(-PAST_MONTHS_INIT);
  // Ref-based in-flight guards prevent stale-closure double-fires
  const isLoadingPastRef  = useRef(false);
  const isLoadingFutureRef = useRef(false);

  // ── Initial load ──
  useEffect(() => {
    if (!uid) return;
    (async () => {
      const allDays = monthList.flatMap((m) => monthGridDays(m));
      const start   = monthList[0];
      const end     = endOfMonthExclusive(monthList[monthList.length - 1]);
      const [cfgs, fetched] = await Promise.all([
        fetchDayConfigsForDates(allDays),
        fetchTutorBlocksInRange(uid, start, end),
      ]);
      setDayConfigsMap(cfgs);
      setBlocks(fetched);
    })();
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scroll to current month on first paint ──
  useEffect(() => {
    const timer = setTimeout(() => {
      currentMonthRef.current?.scrollIntoView({ block: "start", behavior: "instant" });
    }, 60);
    return () => clearTimeout(timer);
  }, []);

  // ── Full reload (post save / delete) ──
  const fullReload = useCallback(async () => {
    if (!uid) return;
    const allDays = monthList.flatMap((m) => monthGridDays(m));
    const start   = monthList[0];
    const end     = endOfMonthExclusive(monthList[monthList.length - 1]);
    const [cfgs, fetched] = await Promise.all([
      fetchDayConfigsForDates(allDays),
      fetchTutorBlocksInRange(uid, start, end),
    ]);
    setDayConfigsMap(cfgs);
    setBlocks(fetched);
  }, [uid, monthList]);

  // ── Load more future months (append below) ──
  const loadMore = useCallback(async () => {
    if (!uid || isLoadingFutureRef.current) return;
    isLoadingFutureRef.current = true;
    setIsLoadingMore(true);

    const prevEnd = loadedEndRef.current;
    const newEnd  = prevEnd + LOAD_MORE_BY;

    try {
      const newStart = addMonths(BASE, prevEnd + 1);
      const newEnd_d = endOfMonthExclusive(addMonths(BASE, newEnd));

      const newMonths = [];
      let m = newStart;
      const stopMs = addMonths(BASE, newEnd).getTime();
      while (m.getTime() <= stopMs) {
        newMonths.push(new Date(m));
        m = addMonths(m, 1);
      }
      const newDays = newMonths.flatMap((mo) => monthGridDays(mo));

      const [newCfgs, newFetched] = await Promise.all([
        fetchDayConfigsForDates(newDays),
        fetchTutorBlocksInRange(uid, newStart, newEnd_d),
      ]);

      setDayConfigsMap((prev) => ({ ...prev, ...newCfgs }));
      setBlocks((prev) => {
        const seen = new Set(prev.map((b) => b.id));
        return [...prev, ...newFetched.filter((b) => !seen.has(b.id))];
      });
      loadedEndRef.current = newEnd;
      setFutureCount(newEnd);
    } catch (err) {
      console.error("loadMore error:", err);
    } finally {
      isLoadingFutureRef.current = false;
      setIsLoadingMore(false);
    }
  }, [uid, BASE]);

  // ── Load more past months (prepend above, zero-jump) ──
  //
  // Strategy: flushSync forces React to commit DOM changes synchronously,
  // so we can read the new scrollHeight and correct scrollTop in the same
  // JS execution — before the browser ever paints. No useLayoutEffect
  // needed, no secondary render for a loading indicator, no visible jump.
  const loadMorePast = useCallback(async () => {
    if (!uid || isLoadingPastRef.current) return;
    isLoadingPastRef.current = true;

    const prevStart = loadedStartRef.current;
    const newStart  = prevStart - LOAD_MORE_PAST;

    try {
      const rangeStart = addMonths(BASE, newStart);
      const rangeEnd   = endOfMonthExclusive(addMonths(BASE, prevStart - 1));

      const newMonths = [];
      for (let i = newStart; i < prevStart; i++) {
        newMonths.push(addMonths(BASE, i));
      }
      const newDays = newMonths.flatMap((m) => monthGridDays(m));

      const [newCfgs, newFetched] = await Promise.all([
        fetchDayConfigsForDates(newDays),
        fetchTutorBlocksInRange(uid, rangeStart, rangeEnd),
      ]);

      const el = containerRef.current;
      if (!el) return;

      // Snapshot the first visible month block so we can preserve exact viewport position.
      const anchor = captureViewportAnchor(el);

      // Commit all state changes to DOM synchronously in one pass
      flushSync(() => {
        setDayConfigsMap((prev) => ({ ...prev, ...newCfgs }));
        setBlocks((prev) => {
          const seen = new Set(prev.map((b) => b.id));
          return [...newFetched.filter((b) => !seen.has(b.id)), ...prev];
        });
        loadedStartRef.current = newStart;
        setPastCount((prev) => prev + LOAD_MORE_PAST);
      });

      // Restore by anchor rather than raw scrollHeight diff.
      // This prevents perceptible jumps when React/layout timing varies.
      if (anchor?.key) {
        requestAnimationFrame(() => {
          const containerRect = el.getBoundingClientRect();
          const anchorNode = el.querySelector(`.mav-month-block[data-month-key="${anchor.key}"]`);
          if (!anchorNode) return;
          const nextTop = anchorNode.getBoundingClientRect().top - containerRect.top;
          el.scrollTop += nextTop - anchor.top;
        });
      }
    } catch (err) {
      console.error("loadMorePast error:", err);
    } finally {
      isLoadingPastRef.current = false;
    }
  }, [uid, BASE]);

  // ── Scroll listener — bidirectional progressive loading ──
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      if (scrollTop < 400) {
        loadMorePast();
      }
      if (scrollHeight - scrollTop - clientHeight < 350) {
        loadMore();
      }
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [loadMore, loadMorePast]);

  // ── Date click ──
  const handleDateClick = (date) => {
    if (isDisabledDay(date, dayConfigsMap)) return;

    if (selectedDate && sameYMD(selectedDate, date)) {
      setSelectedDate(null);
      setEditBlock(null);
      setIsLocked(false);
      return;
    }

      setSelectedDate(date);
    const existing = blocks.find((b) => sameYMD(toDate(b.start), date));
    if (existing) {
      setEditBlock(existing);
      setStartMin(toDate(existing.start).getHours() * 60 + toDate(existing.start).getMinutes());
      setEndMin(toDate(existing.end).getHours() * 60 + toDate(existing.end).getMinutes());
      setIsLocked(isWithinSevenDays(toDate(existing.start)));
    } else {
      const bounds = resolveDayTimelineHours(date, dayConfigsMap);
      setEditBlock(null);
      setStartMin(bounds.startMin);
      setEndMin(bounds.endMin);
      setIsLocked(false);
    }
  };

  // ── Save ──
  const handleSave = async () => {
    if (!selectedDate || !uid) return;
    const bounds = resolveDayTimelineHours(selectedDate, dayConfigsMap);
    if (bounds.isClosed) { alert("This day is closed in schedule settings."); return; }
    if (endMin <= startMin) { alert("End time must be after start time."); return; }

    const safeStart = Math.max(bounds.startMin, Math.min(bounds.endMin - 30, startMin));
    const safeEnd   = Math.min(bounds.endMin, Math.max(safeStart + 30, endMin));
    if (safeEnd <= safeStart) { alert("Selected time is outside the configured schedule range."); return; }

    const s = new Date(selectedDate); s.setHours(Math.floor(safeStart / 60), safeStart % 60, 0, 0);
    const e = new Date(selectedDate); e.setHours(Math.floor(safeEnd / 60), safeEnd % 60, 0, 0);

    try {
      if (editBlock) {
        await updateTutorAvailabilityBlock(editBlock.id, { startDate: s, endDate: e });
      } else {
        await createTutorAvailabilityBlock({ uid, startDate: s, endDate: e });
      }
      await fullReload();
      setSelectedDate(null);
      setEditBlock(null);
    } catch (err) {
      console.error(err);
      alert("Error saving availability.");
    }
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!editBlock) return;
    if (!window.confirm("Remove availability for this day?")) return;
    try {
      await deleteTutorAvailabilityBlock(editBlock.id);
      await fullReload();
      setSelectedDate(null);
      setEditBlock(null);
    } catch (err) { console.error(err); }
  };

  const handleCancel = () => {
    setSelectedDate(null);
    setEditBlock(null);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="mav-container">

      {/* ── Sticky top ── */}
      <div className="mav-sticky-top">
        <div className="mav-page-header">
          <h1 className="mav-page-title">Availability</h1>
          <p className="mav-rule-note">
            Set availability 2 weeks ahead&nbsp;·&nbsp;
            Changes within 7 days need admin approval
          </p>
        </div>
        <div className="mav-weekday-row" aria-hidden="true">
          {WEEKDAYS.map((d, i) => <span key={i}>{d}</span>)}
        </div>
      </div>

      {/* ── Scrollable months ── */}
      <div className="mav-months-body" ref={containerRef}>
        {monthList.map((month, mi) => (
          <MonthBlock
            key={`${month.getFullYear()}-${month.getMonth()}`}
            month={month}
            blockMap={blockMap}
            dayConfigsMap={dayConfigsMap}
            selectedDate={selectedDate}
            today={today}
            onDateClick={handleDateClick}
            blockRef={mi === pastCount ? currentMonthRef : null}
          />
        ))}

        {isLoadingMore && (
          <div className="mav-load-more-indicator">
            <span /><span /><span />
          </div>
        )}
      </div>

      {/* ── Bottom sheet ── */}
      {selectedDate && (
        <>
          <div className="mav-overlay" onClick={handleCancel} />

          <div className="mav-sheet">
            <div className="mav-sheet-handle" />

            <div className="mav-sheet-header">
              <h2 className="mav-sheet-title">
                {editBlock ? "Edit Availability" : "Set Availability"}
              </h2>
              <p className="mav-sheet-date">{formatFullDate(selectedDate)}</p>
            </div>

            {editBlock && isLocked && (
              <div className="mav-lock-notice">
                Locked — changes within 7 days require admin approval.
              </div>
            )}

            <div className="mav-field">
              <label className="mav-field-label">Start Time</label>
              <div className="mav-select-wrap">
                <select
                  className="mav-select"
                  value={startMin}
                  onChange={(e) => setStartMin(Number(e.target.value))}
                  disabled={isLocked}
                >
                  {timeOptions.map((minute) => (
                    <option key={minute} value={minute}>{fmtTimeLabel(minute)}</option>
                  ))}
                </select>
                <span className="mav-select-caret">▾</span>
              </div>
            </div>

            <div className="mav-field">
              <label className="mav-field-label">End Time</label>
              <div className="mav-select-wrap">
                <select
                  className="mav-select"
                  value={endMin}
                  onChange={(e) => setEndMin(Number(e.target.value))}
                  disabled={isLocked}
                >
                  {timeOptions.map((minute) => (
                    <option key={minute} value={minute}>{fmtTimeLabel(minute)}</option>
                  ))}
                </select>
                <span className="mav-select-caret">▾</span>
              </div>
            </div>

            <div className="mav-sheet-actions">
              {editBlock ? (
                <>
                  <button className="mav-btn mav-btn--cancel" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button
                    className="mav-btn mav-btn--delete"
                    onClick={handleDelete}
                    disabled={isLocked}
                  >
                    Remove
                  </button>
                  <button
                    className={`mav-btn ${isLocked ? "mav-btn--locked" : "mav-btn--save"}`}
                    onClick={handleSave}
                    disabled={isLocked}
                  >
                    {isLocked ? "Locked" : "Save"}
                  </button>
                </>
              ) : (
                <>
                  <button className="mav-btn mav-btn--cancel" onClick={handleCancel}>
                    Cancel
                  </button>
                  <button className="mav-btn mav-btn--save" onClick={handleSave}>
                    Save
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
